import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Stack,
  Typography,
  Paper,
  useMediaQuery,
  Button,
  Grid,
  Card,
  Chip,
  IconButton,
  Select,
  MenuItem,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Wrapper } from "../wrapper";
import { CustomTable } from "../table";
import { DataColumns as DashboardColumns } from "../../pages/dashboard/constants";
import {
  apiCategortList,
  usageLogs,
  downloadLogs,
  viewLogDashboardSummary,
  apiUsageTrends,
  apiUsageDistribution,
} from "../../services";
import { SelectDropdown, CustomDivider, Calender, TableButton, Loader } from "../";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ApexPieChart from "../../charts/pie-charts/dashboard";
import DateTimeGraph from "../../charts/line-charts/dashboard";
import dayjs from "dayjs";
import { CheckCircle } from "@mui/icons-material";

export const ViewLogs = () => {
  const location = useLocation();
  const { date, selectedApiId } = location.state || {};

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { user } = useSelector((state) => state.auth);

  const [apiUsageTrendData, setApiUsageTrendData] = useState({});
  const [apiUsageDistributionData, setApiUsageDistributionData] = useState({});
  const [apiCategoryAndList, setApiCategoryAndList] = useState({});
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedAPI, setSelectedAPI] = useState(selectedApiId || "");
  const [isCalendarChanged, setIsCalendarChanged] = useState(false);
  const [dashboardUsageData, setDashboardUsageData] = useState([]);
  const [usageSummary, setUsageSummary] = useState(null);
  const [viewLogDashboardData, setViewLogDashboardData] = useState({});
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  const currentDate = dayjs(date);
  const sevenDaysBack = currentDate.subtract(7, "day");
  const defaultDateRange = [
    sevenDaysBack.startOf("day"),
    currentDate.endOf("day"),
  ];
  const [dateRangeValue, setDateRangeValue] = useState(defaultDateRange);
  const [dashboardDates, setDashboardDates] = useState({
    startDate: defaultDateRange[0].format("YYYY-MM-DD"),
    endDate: defaultDateRange[1].format("YYYY-MM-DD"),
  });

  useEffect(() => {
    const getApiCategoryList = async () => {
      try {
        const res = await apiCategortList();
        if (res?.status === 200) {
          const categories = res?.data?.data?.attributes?.apisByCategory || {};
          setApiCategoryAndList(categories);
        }
      } catch (error) {
        console.error("Error fetching API categories:", error);
      }
    };
    getApiCategoryList();
  }, []);

  const fetchAllViewLogsData = async () => {
    try {
      setLoading(true);
  
      const apiUsageTrendRes = await apiUsageTrends(selectedCategory === "all" ? null : selectedAPI, dashboardDates);
      if (apiUsageTrendRes?.status === 200) {
        setApiUsageTrendData(apiUsageTrendRes?.data);
      }

      const apiUsageDistributionRes = await apiUsageDistribution(selectedCategory === "all" ? null : selectedAPI, dashboardDates);
      if (apiUsageDistributionRes?.status === 200) {
        setApiUsageDistributionData(apiUsageDistributionRes?.data);
      }

      const usageLogsRes = await usageLogs({
        startDate: dashboardDates?.startDate,
        endDate: dashboardDates?.endDate,
        apiId: selectedCategory === "all" ? null : selectedAPI,
      });
      if (usageLogsRes?.status === 200) {
        const resBody = usageLogsRes?.data;
        const usageData = resBody?.data ?? resBody;
        setDashboardUsageData(Array.isArray(usageData) ? usageData : []);
        setUsageSummary(resBody?.summary ?? null);
      }

      const viewLogData = await viewLogDashboardSummary(
        dashboardDates?.startDate,
        dashboardDates?.endDate
      );
      if (viewLogData?.status === 200) {
        setViewLogDashboardData(viewLogData?.data || {});
      }
    } catch (error) {
      console.error("Error fetching view logs data:", error);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchAllViewLogsData();
  }, [selectedAPI, selectedCategory, dashboardDates]);

  useEffect(() => {
    if (
      selectedCategory !== "all" &&
      apiCategoryAndList[selectedCategory]?.apis
    ) {
      const sortedApis = apiCategoryAndList[selectedCategory]?.apis.sort(
        (a, b) => a.name.localeCompare(b.name)
      );
      setSelectedAPI(sortedApis[0]?.id || "");
    } else if (selectedCategory === "all") {
      setSelectedAPI("");
    }
  }, [selectedCategory, apiCategoryAndList, isCalendarChanged]);

  const { apiOptions } = useMemo(() => {
    const selected = apiCategoryAndList[selectedCategory] || [];
    if (selected?.apis?.length > 0) {
      const sortedApiOptions = selected?.apis
        .map((item) => ({
          label: item.name,
          value: item.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
      return { apiOptions: sortedApiOptions };
    }
    return { apiOptions: [] };
  }, [selectedCategory, apiCategoryAndList]);

  const handleChangeCalender = (dates) => {
    if (dates) {
      setDashboardDates({
        startDate: dates[0].format("YYYY-MM-DD"),
        endDate: dates[1].format("YYYY-MM-DD"),
      });
      setDateRangeValue(dates);
      setIsCalendarChanged(true);
    } else {
      setDashboardDates({
        startDate: defaultDateRange[0].format("YYYY-MM-DD"),
        endDate: defaultDateRange[1].format("YYYY-MM-DD"),
      });
      setDateRangeValue(defaultDateRange);
      setIsCalendarChanged(true);
    }
  };

  const handleDownloadLogs = async (id) => {
    try {
      const response = await downloadLogs(
        selectedCategory === "all" ? null : id,
        dashboardDates
      );
      if (response?.data) {
        const url = URL.createObjectURL(response?.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = "logs.xlsx";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const totalApiCalls = viewLogDashboardData?.totalApiCalls ?? {};
  const creditsConsumed = viewLogDashboardData?.creditsConsumed ?? {};
  const avgResponseTime = viewLogDashboardData?.avgResponseTime ?? {};

  const formatChange = (change) => {
    if (change === null || change === undefined) return undefined;
    const isPositive = change >= 0;
    return {
      value: `${isPositive ? "+" : ""}${change}%`,
      isPositive,
    };
  };

  const stats = [
    {
      title: "Total API Calls",
      value: totalApiCalls.current ?? 0,
      change: formatChange(totalApiCalls.changePercent),
      color: "#6366f1",
      icon: (
        <ShowChartIcon
          sx={{ width: "2.5rem", height: "2.5rem", color: "#6366f1" }}
        />
      ),
    },
    {
      title: "Success Rate",
      value: apiUsageDistributionData?.pieChart?.successRate 
        ? `${apiUsageDistributionData.pieChart.successRate}%`
        : "97.9%",
      change: apiUsageDistributionData?.pieChart?.successRateChange
        ? formatChange(apiUsageDistributionData.pieChart.successRateChange)
        : { value: "+0.3%", isPositive: true },
      color: "#10b981",
      icon: (
        <CheckCircle
          sx={{ width: "2.5rem", height: "2.5rem", color: "#10b981" }}
        />
      ),
    },
    {
      title: "Credits Consumed",
      value: creditsConsumed.current ?? 0,
      change: formatChange(creditsConsumed.changePercent),
      color: "#8b5cf6",
      icon: (
        <CalendarMonthIcon
          sx={{ width: "2.5rem", height: "2.5rem", color: "#8b5cf6" }}
        />
      ),
    },
    {
      title: "Avg Response Time",
      value: `${avgResponseTime.current ?? 0}s`,
      change: formatChange(avgResponseTime.changePercent),
      color: "#8b5cf6",
      icon: (
        <AccessTimeIcon
          sx={{ width: "2.5rem", height: "2.5rem", color: "#8b5cf6" }}
        />
      ),
    },
  ];

  const [days, setDays] = React.useState("7");
  const [calendarOpen, setCalendarOpen] = useState(false);
  const handleDaysChange = (value) => {
    setDays(value);

    if (value === "custom") {
      setCalendarOpen(true);
      return;
    }

    setCalendarOpen(false);

    const endDate = dayjs();
    const startDate = endDate.subtract(Number(value), "day");

    const newRange = [startDate.startOf("day"), endDate.endOf("day")];
    setDateRangeValue(newRange);
    setDashboardDates({
      startDate: newRange[0].format("YYYY-MM-DD"),
      endDate: newRange[1].format("YYYY-MM-DD"),
    });
    setIsCalendarChanged(true);
  };

  return (
    <Box>
      <Wrapper
        sx={{ paddingTop: theme.spacing(8), marginTop: { xs: "30px", md: 0 } }}>
        {loading && <Loader />}
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.textColor?.bluishgray || "#65758B",
                  fontWeight: 400,
                  fontSize: "16px",
                }}
              >
                Detailed analytics and insights for your API usage
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: "flex",
                justifyContent: { xs: "flex-start", md: "flex-end" },
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Box display="flex" flexDirection="row" gap={1}>
                <Select
                  value={days}
                  onChange={(e) => handleDaysChange(e.target.value)}
                  size="small"
                  sx={{
                    borderRadius: "8px",
                    fontSize: "14px",
                    height: "36px",
                    bgcolor: "background.paper",
                    boxShadow: 1,
                    "& .MuiSelect-select": {
                      display: "flex",
                      alignItems: "center",
                    },
                  }}
                >
                  <MenuItem value="7">Last 7 days</MenuItem>
                  <MenuItem value="30">Last 30 days</MenuItem>
                  <MenuItem value="90">Last 90 days</MenuItem>
                  <MenuItem value="custom">Custom Range</MenuItem>
                </Select>

                {/* Calendar opens inline below the select */}
                {calendarOpen && (
                  <Box>
                    <Calender
                      handleChange={(dates) => {
                        if (dates) {
                          handleChangeCalender(dates);
                        }
                      }}
                      defaultValue={defaultDateRange}
                      value={dateRangeValue}
                      maxDate={new Date()}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
        <Grid container spacing={2} mt={1}>
          {stats.map((stat) => (
            <Grid item xs={12} sm={6} lg={3} key={stat.title}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  height: "100%",
                  boxShadow: "none",
                  border: "1px solid #E5EAF2",
                  padding: "20px",
                }}
              >
                <Box>
                  {/* Title + Optional Icon */}
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.textColor.verydarkblue,
                        fontWeight: 700,
                        fontSize: "1.5rem",
                      }}
                    >
                      {stat.title}
                    </Typography>
                    {stat.icon && (
                      <IconButton sx={{ padding: "8px", backgroundColor: `${stat.color}15` }}>{stat.icon}</IconButton>
                    )}
                    {stat.badge && (
                      <Chip
                        label={stat.badge}
                        size="small"
                        sx={{
                          backgroundColor:
                            stat.badge === "Good" ? "#DFF7E8" : "#F3F4F6",
                          color: stat.badge === "Good" ? "#1A7F37" : "#111",
                          fontWeight: 500,
                          height: "22px",
                        }}
                      />
                    )}
                  </Box>

                  {/* Main Value + Optional Badge */}
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Typography
                      variant="h5"
                      sx={{
                        color: theme.palette.textColor.verydarkblue,
                        fontWeight: 700,
                        fontSize: "22px",
                      }}
                    >
                      {stat.value}
                    </Typography>
                  </Box>

                  {/* Subtext */}
                  {stat.change && (
                    <Typography
                      variant="caption"
                      component="div"
                      sx={{
                        fontWeight: 400,
                        fontSize: "1.2rem",
                      }}
                    >
                      <Box component="span" sx={{ color: stat.change.isPositive ? "#10b981" : "#ef4444" }}>
                        {stat.change.value}
                      </Box>
                      <Box component="span" sx={{ color: theme.palette.textColor.bluishgray, ml: 0.5 }}>
                        from last period
                      </Box>
                    </Typography>
                  )}
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Typography
          sx={{
            color: theme.palette.textColor?.verydarkblue || "#020817",
            fontWeight: 700,
            fontSize: "26px",
            mb: 3,
            mt: 3,
          }}
          variant="h4"
        >
          Usage Summary
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: "1rem",
            mb: 3,
          }}
        >
          <Stack
            spacing={1}
            sx={{ width: { xs: "100%", sm: "auto", md: "auto" } }}
          >
            <Typography
              variant="body1"
              sx={{ color: theme.palette.grey.darken4 }}
            >
              Type of API
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: "1.5rem" }}>
              <SelectDropdown
                menuOptions={[
                  { label: "All", value: "all" },
                  ...Object.keys(apiCategoryAndList)
                    .sort()
                    .map((category) => ({
                      label: category,
                      value: category,
                    })),
                ]}
                value={selectedCategory}
                onChange={(value) => setSelectedCategory(value)}
                widthSize={isSmallScreen ? "100%" : "22rem"}
              />
              <CustomDivider orientation="vertical" />
            </Box>
          </Stack>

          <Stack
            spacing={1}
            sx={{ width: { xs: "100%", sm: "auto", md: "auto" } }}
          >
            <Typography
              variant="body1"
              sx={{ color: theme.palette.grey.darken4 }}
            >
              APIs
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: "1.5rem" }}>
              <SelectDropdown
                menuOptions={apiOptions}
                value={selectedAPI}
                onChange={setSelectedAPI}
                widthSize={isSmallScreen ? "100%" : "22rem"}
                disabled={selectedCategory === "all"}
              />
            </Box>
          </Stack>
        </Box>

        <Grid container spacing={3} mb={4}>
          {/* API Usage Trends */}
          <Grid item xs={12} lg={8}>
            <Paper
              sx={{
                borderRadius: "16px",
                boxShadow:
                  "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
                border: "1px solid #f1f5f9",
                p: 2,
                height: "100%",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "#1e293b",
                  mb: 2,
                  fontSize: "18px",
                }}
              >
                API Usage Trends
              </Typography>
                <DateTimeGraph data={apiUsageTrendData} />
            </Paper>
          </Grid>

          {/* Usage Distribution */}
          <Grid item xs={12} lg={4}>
            <Paper
              sx={{
                borderRadius: "16px",
                boxShadow:
                  "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
                border: "1px solid #f1f5f9",
                p: 2,
                height: "100%",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "#1e293b",
                  mb: 2,
                  fontSize: "18px",
                }}
              >
                Usage Distribution
              </Typography>

              <Box
                sx={{
                  height: 280,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ApexPieChart
                  piechartData={
                    apiUsageDistributionData?.pieChart || null
                  }
                />
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: 5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
              mb: 2,
            }}
          >
            <Typography
              sx={{
                color: theme.palette.textColor?.verydarkblue || "#020817",
                fontWeight: 700,
                fontSize: "26px",
              }}
              variant="h5"
            >
              Usage Logs
            </Typography>
            <Button
              onClick={() => handleDownloadLogs(selectedAPI)}
              variant="contained"
              disabled={
                !Array.isArray(dashboardUsageData) ||
                dashboardUsageData.length === 0 ||
                dashboardUsageData.every((entry) => entry.apiUsed === 0)
              }
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Download logs
            </Button>
          </Box>
          <CustomTable
            columns={DashboardColumns}
            data={[
              ...(Array.isArray(dashboardUsageData) ? dashboardUsageData : []).map((item) => ({
                ...item,
                usage: (
                  <TableButton
                    key={item?.date}
                    onClick={() => {
                      navigate(`/usage-reports/access-logs/${item?.date}`, {
                        state: {
                          selectedApiId: selectedAPI,
                          selectedCategory: selectedCategory,
                        },
                      });
                    }}
                  >
                    Access Logs
                  </TableButton>
                ),
                key: item?.date,
              })),
              ...(usageSummary ? [{
                date: "Total",
                apiUsed: usageSummary.apiUsed,
                totalRequest: usageSummary.totalRequest,
                successCount: usageSummary.successCount,
                failureCount: usageSummary.failureCount,
                successRate: usageSummary.avgSuccessRate,
                creditsUtilized: usageSummary.creditsUtilized,
                usage: null,
                isTotal: true,
                key: "total",
              }] : []),
            ]}
          />
        </Box>
      </Wrapper>
    </Box>
  );
}; 